import { useNavigate } from 'react-router-dom';
import { useGameStore } from '../stores/gameStore';
import { useUserStore } from '../stores/userStore';
import { getTitleForScore } from '@beast-keeper/shared';
import { spawnConfetti } from '../utils/confetti';
import { submitGameResult } from '../services/questionService';
import { sfxNewTitle } from '../utils/audio';
import { useState, useEffect } from 'react';

export default function ResultScreen() {
  const navigate = useNavigate();
  const { getResult, questions, reset } = useGameStore();
  const { bestScore, updateBestScore, nickname, token, fetchProfile } = useUserStore();
  const [loaded, setLoaded] = useState(false);
  const [serverResult, setServerResult] = useState<any>(null);
  const [syncFailed, setSyncFailed] = useState(false);
  const [showTitlePopup, setShowTitlePopup] = useState(true);

  const result = getResult();
  const title = result.title;
  const isNewRecord = result.score > bestScore;

  useEffect(() => {
    const submitAndShow = async () => {
      // Optimistically update local best
      if (isNewRecord) {
        updateBestScore(result.score, title.name);
        sfxNewTitle();
        spawnConfetti('confetti');
      }

      // Resolve nickname: if missing but token exists, try fetching profile
      let resolvedNickname = nickname;
      if (!resolvedNickname && token) {
        await fetchProfile();
        resolvedNickname = useUserStore.getState().nickname;
      }

      // Submit to server
      if (resolvedNickname) {
        try {
          const res = await submitGameResult({
            score: result.score,
            title: title.name,
            questionIds: questions.map((q) => q.id),
            answers: result.answers,
            timeTakenMs: result.timeTakenMs,
          });
          setServerResult(res);
          if (res.isNewRecord && res.beatPercent !== undefined) {
            updateBestScore(res.score, res.title);
          }
        } catch (e) {
          console.error('[ResultScreen] 提交分数失败', e);
          setSyncFailed(true);
        }
      } else {
        setSyncFailed(true);
      }

      setLoaded(true);
    };

    submitAndShow();
  }, []);

  // Score counting animation
  const [displayScore, setDisplayScore] = useState(0);
  useEffect(() => {
    const duration = 1200;
    const start = performance.now();
    const target = result.score;

    function animate(now: number) {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplayScore(Math.round(eased * target));
      if (progress < 1) requestAnimationFrame(animate);
    }
    requestAnimationFrame(animate);
  }, [result.score]);

  const beatPct = serverResult?.beatPercent ?? 50;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, padding: 'var(--space-lg) var(--space-md)' }}>
      <div className="scroll-area">
        <div className="result-content">
          <div className="result-score-label">本次得分</div>
          <div className="result-score-num">{displayScore}</div>
          <div className="result-title-icon">{title.icon}</div>
          <div className="result-title-name">{title.name}</div>
          <div className="result-title-quote">"{title.quote}"</div>

          <div className="result-stats">
            {serverResult ? (
              <>
                全站排名 <strong>#{serverResult.rank?.all ?? '-'}</strong>
                {' / '}{serverResult.totalPlayers ?? '-'}
                {' | '}
                击败了 <strong>{beatPct}%</strong> 的饲养员
              </>
            ) : syncFailed ? (
              <span style={{ color: 'var(--color-text-secondary)' }}>
                {nickname ? '排名同步失败，请稍后重试' : '未登录，无法同步排名'}
              </span>
            ) : (
              <span style={{ color: 'var(--color-text-secondary)' }}>正在同步排名...</span>
            )}
          </div>

          {isNewRecord ? (
            <div className="new-record-badge">🎉 新纪录！撒花！🎉</div>
          ) : (
            <div className="result-old-best">历史最高分：{bestScore} 分</div>
          )}
        </div>

        {/* Title earned popup */}
        {loaded && showTitlePopup && (
          <div className="modal-overlay show">
            <div className="modal-dialog" style={{ textAlign: 'center' }}>
              <span style={{ fontSize: '4rem', display: 'block', marginBottom: 'var(--space-md)' }}>
                {title.icon}
              </span>
              <h3 style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                {title.name}
              </h3>
              <p className="hint">"{title.quote}"</p>
              <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.9rem', marginTop: 'var(--space-sm)' }}>
                本次得分：{displayScore} 分
              </p>
              {isNewRecord && (
                <p style={{ color: 'var(--color-accent)', fontSize: '1rem', marginTop: 'var(--space-sm)' }}>
                  🎉 新纪录！
                </p>
              )}
              <button
                className="btn btn-primary"
                style={{ marginTop: 'var(--space-lg)', width: '100%' }}
                onClick={() => setShowTitlePopup(false)}
              >
                查看详情
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      {loaded && !showTitlePopup && (
        <div className="result-actions">
          <button className="btn btn-primary btn-lg" onClick={() => { reset(); navigate('/quiz'); }}>
            🔄 再来一局
          </button>
          <div style={{ display: 'flex', gap: 'var(--space-sm)' }}>
            <button className="btn btn-secondary btn-sm" style={{ flex: 1 }} onClick={() => navigate('/leaderboard')}>
              🏆 排行榜
            </button>
            <button className="btn btn-secondary btn-sm" style={{ flex: 1 }} onClick={() => navigate('/')}>
              🏠 回首页
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
